// Copyright (c) Jupyter Development Team.
// Distributed under the terms of the Modified BSD License.
import { find, iter } from '@lumino/algorithm';
import { Token } from '@lumino/coreutils';
import { Signal } from '@lumino/signaling';
import { Panel, Widget, PanelLayout } from '@lumino/widgets';
/**
 * The single widget application shell token.
 */
export const ISingleWidgetShell = new Token('@jupyterlite/application:ISingleWidgetShell');
/**
 * The application shell.
 */
export class SingleWidgetShell extends Widget {
    constructor() {
        super();
        this._currentChanged = new Signal(this);
        this.id = 'main';
        const rootLayout = new PanelLayout();
        this._main = new Panel();
        this._main.id = 'single-widget-panel';
        rootLayout.addWidget(this._main);
        this.layout = rootLayout;
    }
    /**
     * A signal emitted when the current widget changes.
     */
    get currentChanged() {
        return this._currentChanged;
    }
    /**
     * The current widget in the shell's main area.
     */
    get currentWidget() {
        var _a;
        return (_a = this._main.widgets[0]) !== null && _a !== void 0 ? _a : null;
    }
    /**
     * Activate a widget in its area.
     */
    activateById(id) {
        const widget = find(this.widgets('main'), (w) => w.id === id);
        if (widget) {
            widget.activate();
        }
    }
    /**
     * Add a widget to the application shell.
     *
     * @param widget - The widget being added.
     *
     * @param area - Optional region in the shell into which the widget should
     * be added.
     *
     * @param options - Optional open options.
     *
     */
    add(widget, area, options) {
        if (area === 'main' || area === undefined) {
            if (this._main.widgets.length > 0) {
                // do not add the widget if there is already one
                return;
            }
            this._main.addWidget(widget);
            this._main.update();
            this._currentChanged.emit(void 0);
        }
    }
    /**
     * Return the list of widgets for the given area.
     *
     * @param area The area
     */
    widgets(area) {
        switch (area !== null && area !== void 0 ? area : 'main') {
            case 'main':
                return iter(this._main.widgets);
            default:
                throw new Error(`Invalid area: ${area}`);
        }
    }
}
